import Squad from "@skrice.studio/mavia-common/build/models/main/Squad";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import SearchIcon from "@mui/icons-material/Search";
import {
    Autocomplete,
    Box,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow, TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import axios from "axios";
import * as React from "react";
import ReactCountryFlag from "react-country-flag";
import { NavLink } from "react-router-dom";
import { CoatOfArms } from "../../components/CoatOfArms";
import { LoadingWrapper } from "../../components/LoadingWrapper";
import { Config } from "../../config/config";
import { CountryCodes } from "../../utils/countryCodes";
import { Helpers } from "../../utils/helpers";

interface AlliancesStates {
    squads: any[];
    loading: boolean;
    filtersExpanded: boolean;
    filters: AllianceSearchFilters,
    filter: AllianceSearchFilter,
    form: AllianceSearchForm;
    totalRecords: number;
    pushModalVisible: boolean;
    selectedPlayer: any | null;
    deviceModalVisible: boolean;
    rowsPerPage: number;
    page: number;
}

interface AllianceSearchFilter {
    type: string | null;
    country: string | null;
}

export interface AllianceSearchFilters {
    famepointsOperation: string;
    famepoints: string;
    memberCountOperation: string;
    memberCount: string;
}

export interface AllianceSearchForm {
    keyword: string | undefined;
    inapp: boolean;
    hq_level: number | undefined;
    sort: string;
}

export interface SearchFormProps {
    onFormSubmit(): void;
}

interface PlayersProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
}

export class Alliances extends React.Component<PlayersProps, AlliancesStates> {

    constructor(props: PlayersProps) {
        super(props);
        // List with grid items
        this.state = {
            squads: [],
            loading: true,
            filtersExpanded: false,
            totalRecords: 0,
            filters: {
                famepoints: "",
                famepointsOperation: "",
                memberCount: "",
                memberCountOperation: "",
            },
            filter: {
                type: null,
                country: null,
            },
            form: {
                keyword: undefined,
                inapp: false,
                hq_level: undefined,
                sort: "joined_ASC",
            },
            pushModalVisible: false,
            selectedPlayer: null,
            deviceModalVisible: false,
            // Pagination
            page: 0,
            rowsPerPage: 10,
        };

        document.title = Helpers.documentTitle("Alliances");
    }

    componentDidMount() {
        this.fetchAlliances();
    }

    fetchAlliances = () => {
        const filteredFilters = Object.entries(this.state.filter).reduce((filters, [key, filter]) => {
            if (typeof filter === "object") {
                filters[key] = filter?.code || null;
            } else {
                filters[key] = filter;
            }
            return filters;
        }, {});

        axios.post(`${Config.getInstance.apiUrl}/api/alliances`, {
            filters: {
               ...this.state.filters,
               ...filteredFilters,
            },
            ...this.state.form,
            page: this.state.page,
            rowsPerPage: this.state.rowsPerPage,
        }, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            },
        }).then(response => {
            this.setState({
                loading: false,
                squads: response.data.data || [],
                totalRecords: response.data.total,
            });
        });
    };

    searchInput = (e: any) => {
        const tempForm = {...this.state.form};
        tempForm.keyword = e.target.value;

        this.setState({form: tempForm});
    };

    handleSort = (e: any) => {
        const tempForm = {...this.state.form};
        tempForm.sort = e.target.value;

        this.setState({form: tempForm});
    };

    searchForm = () => this.setState({loading: true}, () => {
        this.fetchAlliances();
    });

    handleChangePage = (_event: any, page: number) => this.setState({loading: true, page}, () => {
        this.fetchAlliances();
    });

    handleChangeRowsPerPage = (event: any) => this.setState({
        loading: true,
        page: 0,
        rowsPerPage: event.target.value,
    }, () => {
        this.fetchAlliances();
    });

    formatPaginationRange = ({from, to}) => `${from}-${to} of ${this.state.totalRecords}`;

    formatType(type: number) {
        switch (type) {
            case 1:
                return "Open for all";
            case 2:
                return "Invites only";
            case 3:
                return "Closed";
            default:
                return "Unknown type";
        }
    }

    formatTableBody = (items: Squad[]) => {
        if (this.state.loading) {
            return <TableRow key="loading-row">
                <TableCell colSpan={10}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        return items.map(item => (
            <TableRow
                key={item.id}
                hover={true}
            >
                <TableCell style={{ width: 82 }}>
                    <CoatOfArms emblem={item.flag}/>
                </TableCell>
                <TableCell>
                    <NavLink to={`/alliances/${item.id}`}>
                        {item.name}
                    </NavLink>
                </TableCell>
                <TableCell>{this.formatType(item.type)}</TableCell>
                <TableCell>{item.tag}</TableCell>
                <TableCell>{item.members_count}/{item.max_members}</TableCell>
                <TableCell>{item.current_rank}</TableCell>
                <TableCell>{item.gold_coins}</TableCell>
                <TableCell>{item.tech_points}</TableCell>
                <TableCell>{item.black_credits}</TableCell>
                <TableCell>{new Date(item.created_at).toLocaleString()}</TableCell>
            </TableRow>
        ));
    };

    handleFilter = (e: any) => {
        const data = {...this.state.filters};
        data[e.target.name] = e.target.value;

        this.setState({
            filters: data,
        });
    }

    applyFilters = () => {
        this.fetchAlliances()
    }

    clearFilters = () => {
        this.setState({
            filters: {
                famepoints: "",
                famepointsOperation: "",
                memberCount: "",
                memberCountOperation: "",
            },
            filter: {
                type: null,
                country: null,
            },
            form: {
                keyword: undefined,
                inapp: false,
                hq_level: undefined,
                sort: "joined_ASC",
            },
            loading: true
        }, () => {
            this.fetchAlliances()
        })
    }

    handleFilters(property: string, value: any) {
        const filterForm = {...this.state.filter};
        filterForm[property] = value;

        this.setState({filter: filterForm});
    }

    pageContent = (items) => {
        if (!this.state.loading && items.length === 0) {
            return <div className="no-items-placeholder">
                <NotInterestedIcon/>
                <h3>No alliances found</h3>
            </div>;
        }

        return <React.Fragment>
            <Table style={{ minWidth: 700 }}>
                <TableHead>
                    <TableRow>
                        <TableCell style={{ width: 82 }}></TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell style={{width: 120}}>Type</TableCell>
                        <TableCell style={{width: 120}}>Tag</TableCell>
                        <TableCell style={{width: 120}}>Members</TableCell>
                        <TableCell style={{width: 120}}>Rank</TableCell>
                        <TableCell style={{width: 120}}>Gold Coins</TableCell>
                        <TableCell style={{width: 120}}>Tech Points</TableCell>
                        <TableCell style={{width: 120}}>Black Credits</TableCell>
                        <TableCell style={{width: 200}}>Created at</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.formatTableBody(items)}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            count={this.state.totalRecords}
                            rowsPerPage={this.state.rowsPerPage}
                            page={this.state.page}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                            labelDisplayedRows={this.formatPaginationRange}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </React.Fragment>
    }

    render() {
        const {classes} = this.props;

        return <React.Fragment>
            <Paper elevation={0} className="page-filters">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            placeholder="Search…"
                            onKeyUp={this.searchInput}
                            style={{ width: "100%" }}
                            onKeyDown={(e) => e.key === "Enter" ? this.searchForm() : null}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item sm={12}>
                        <Typography variant="h6">
                            Filters
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl style={{width: "100%"}} className="with-select">
                            <InputLabel htmlFor="famepoints-compare-filter">
                                Famepoints
                            </InputLabel>

                            <Select
                                value={this.state.filters.famepointsOperation}
                                onChange={this.handleFilter}
                                style={{width: "100%"}}
                                label="Famepoints"
                                size="small"
                                inputProps={{
                                    name: "famepointsOperation",
                                    id: "famepoints-compare-filter",
                                }}
                            >
                                <MenuItem id="" value="" disabled>Operation</MenuItem>
                                <MenuItem value="eq">Equal</MenuItem>
                                <MenuItem value="lte">Lower or equal than</MenuItem>
                                <MenuItem value="gte">Greater or equal than</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {this.state.filters.famepointsOperation && <Grid item xs={12}>
                        <TextField
                            name="famepoints"
                            id="outlined-basic"
                            label="Amount"
                            type="number"
                            size="small"
                            value={this.state.filters.famepoints}
                            onChange={this.handleFilter}
                            style={{width: "100%"}}
                        />
                    </Grid>}

                    <Grid item xs={12}>
                        <Autocomplete
                            onChange={(_, newValue) => this.handleFilters("type", newValue)}
                            value={this.state.filter.type || ""}
                            options={[{ code: 1, label: "Open for all" }, { code: 2, label: "Invites only" }, { code: 3, label: "Closed" }] as any[]}
                            getOptionLabel={option => option?.label || ""}
                            disableClearable={true}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="Alliance type"
                                    variant="outlined"
                                    size="small"
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            onChange={(_, newValue) => this.handleFilters("country", newValue)}
                            value={this.state.filter.country || ""}
                            options={CountryCodes.sort((a, b) => -b.name.localeCompare(a.name))}
                            getOptionLabel={option => option?.name || ""}
                            disableClearable={true}
                            renderOption={(props, item) => (
                                <Box component="li" {...props}>
                                    <ReactCountryFlag countryCode={item.code} svg={true}/>
                                    <span style={{ marginLeft: 7, fontSize: 15 }}>{item.name}</span>
                                </Box>
                            )}
                            renderInput={(params) =>
                                <TextField {...params}
                                label="Country"
                                variant="outlined"
                                size="small"
                            />}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl style={{width: "100%"}} className="with-select">
                            <InputLabel htmlFor="member-count-filter">
                                Members
                            </InputLabel>
                            <Select
                                value={this.state.filters.memberCountOperation}
                                onChange={this.handleFilter}
                                label="Members"
                                size="small"
                                inputProps={{
                                    name: "memberCountOperation",
                                    id: "member-count-filter",
                                }}
                            >
                                <MenuItem id="" value="" disabled>Operation</MenuItem>
                                <MenuItem value="eq">Equal</MenuItem>
                                <MenuItem value="lte">Lower or equal than</MenuItem>
                                <MenuItem value="gte">Greater or equal than</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {this.state.filters.memberCountOperation && <Grid item xs={12}>
                        <TextField
                            name="memberCount"
                            id="outlined-basic"
                            label="Amount"
                            size="small"
                            type="number"
                            value={this.state.filters.memberCount}
                            onChange={this.handleFilter}
                            style={{ width: "100%" }}
                        />
                    </Grid>}

                    <Grid item sm={12}>
                        <Typography variant="h6">
                            Sort
                        </Typography>
                    </Grid>

                    <Grid item sm={12}>
                        <Select
                            value={this.state.form.sort}
                            onChange={this.handleSort}
                            size="small"
                            style={{ width: "100%" }}
                        >
                            <MenuItem value="joined_ASC">Created ASC</MenuItem>
                            <MenuItem value="joined_DESC">Created DESC</MenuItem>
                            <MenuItem value="power_ASC">Power ASC</MenuItem>
                            <MenuItem value="power_DESC">Power DESC</MenuItem>
                            <MenuItem value="members_ASC">Members ASC</MenuItem>
                            <MenuItem value="members_DESC">Members DESC</MenuItem>
                        </Select>
                    </Grid>

                    <Grid item xs={6}>
                        <Button
                            disableElevation
                            variant="contained"
                            onClick={this.searchForm}
                            style={{ width: "100%" }}
                        >
                            Search
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            disableElevation
                            variant="contained"
                            color="secondary"
                            style={{ width: "100%" }}
                            onClick={this.clearFilters}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <div className="contentPadding-withFilters" style={{ marginBottom: 25 }}>
                <Paper className="root" elevation={0}>
                    {this.pageContent(this.state.squads)}
                </Paper>
            </div>
        </React.Fragment>
    }
}
